import React from 'react';

import { Anchor, Code, List, Paper, Stack, Text, Title } from '@mantine/core';
import { KILO_PRIVACY_POLICY_URL } from 'utils/external-resources';

const ClaudeSetupInstructions = (): JSX.Element => (
  <Paper shadow='xl' p='md' pb='lg'>
    <Title order={4} mb='sm'>
      How to connect Claude
    </Title>

    <Stack spacing='md'>
      <Text>
        Kilo connects to Claude as a custom connector. You will need a Claude account on a plan that supports
        connectors, and you must be a gym owner to authorize one.
      </Text>

      <List type='ordered' spacing='sm'>
        <List.Item>Copy your gym&apos;s MCP connection URL from the card below.</List.Item>
        <List.Item>
          In Claude, open <b>Settings → Connectors</b> and choose <b>Add custom connector</b>.
        </List.Item>
        <List.Item>
          Paste the URL into the connector&apos;s <b>URL</b> field and give it a name you will recognise,
          such as <Code>Kilo</Code>. Leave the remaining fields empty.
        </List.Item>
        <List.Item>
          Choose <b>Connect</b>. Claude opens Kilo in your browser and asks you to sign in if you are not
          already.
        </List.Item>
        <List.Item>
          Pick the gym you want Claude to read, then choose <b>Authorize</b>. If you own more than one gym,
          each is authorized separately - repeat these steps per gym.
        </List.Item>
        <List.Item>
          Claude returns to its own window and the connection appears under <b>Active connections</b> below.
        </List.Item>
      </List>

      <div>
        <Text fw={500}>What Claude can do once connected</Text>
        <List size='sm' spacing={4} mt={4} withPadding>
          <List.Item>Read reports and analytics for the one gym you authorized - nothing else.</List.Item>
          <List.Item>No access to your other gyms, and no ability to change any data.</List.Item>
          <List.Item>
            Member names come through in full, but email addresses are masked (
            <Code>jo****@example.com</Code>). Phone numbers, addresses, dates of birth, and payment, card and
            billing data are never returned at all.
          </List.Item>
          <List.Item>
            Ask it questions like &ldquo;how did revenue trend last month?&rdquo; or &ldquo;which packages
            are expiring soon?&rdquo;
          </List.Item>
        </List>
      </div>

      <Text size='sm' color='dimmed'>
        Using more than one Claude client (desktop and web) creates one connection per client. That is
        expected, and each can be revoked on its own. Connecting Kilo to an AI provider constitutes
        acceptance of Kilo&apos;s{' '}
        <Anchor
          href={KILO_PRIVACY_POLICY_URL}
          target='_blank'
          rel='noreferrer'
          size='sm'
          data-cy='claudeSetupPolicyLink'
        >
          privacy policy
        </Anchor>
        .
      </Text>
    </Stack>
  </Paper>
);

export default ClaudeSetupInstructions;
